import React from "react";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";

interface EmptyStateProps {
  icon?: string;
  title: string;
  description: string;
  primaryAction?: {
    label: string;
    to: string;
  };
  secondaryAction?: {
    label: string;
    to: string;
  };
  fullScreen?: boolean;
}

const EmptyStateContainer = styled.div<{ fullScreen?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${(props) => (props.fullScreen ? "100vh" : "auto")};
  background: ${(props) => (props.fullScreen ? "#f9fafb" : "transparent")};
  padding: 32px;
  text-align: center;
`;

const EmptyStateCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 480px;
  width: 100%;
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: white;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 36px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px;
  line-height: 1.5;
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #111827;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.4);
    text-decoration: none;
    color: white;
    background: #374151;
  }

  &:focus {
    outline: 2px solid #111827;
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #6b7280;
  padding: 12px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  margin-left: 12px;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    text-decoration: none;
    color: #374151;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const EmptyState: React.FC<EmptyStateProps> = ({
  icon = "📋",
  title,
  description,
  primaryAction,
  secondaryAction,
  fullScreen = true,
}) => {
  return (
    <EmptyStateContainer fullScreen={fullScreen}>
      <EmptyStateCard>
        <IconContainer>{icon}</IconContainer>
        <Title>{title}</Title>
        <Description>{description}</Description>
        <ButtonContainer>
          {primaryAction && (
            <ActionButton to={primaryAction.to}>
              {primaryAction.label}
            </ActionButton>
          )}
          {secondaryAction && (
            <SecondaryButton to={secondaryAction.to}>
              {secondaryAction.label}
            </SecondaryButton>
          )}
        </ButtonContainer>
      </EmptyStateCard>
    </EmptyStateContainer>
  );
};

export default EmptyState;
